'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Asgaardproduct from '../query/Asgaardproduct/page';
import { useCartContext } from "@/context/CartContext";

// Product Interface
interface Product {
  _id: string;
  title: string;
  price: number;
  description: string;
  imageUrl: string;
  images?: string[];
  inventory?: number;
  tags?: string[];
  categoryName?: string;
  sku?: string;
  discountPercentage?: number;
  sizes?: string[];
  colors?: string[];
}

interface ProductClientProps {
  product: Product;
}

const toTitleCase = (str: string) => {
  if (!str) return '';
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
  );
};

export default function ProductClient({ product }: ProductClientProps) {
  const { addToCart } = useCartContext();
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedColor, setSelectedColor] = useState<string>(
    product.colors && product.colors.length > 0 ? product.colors[0] : ''
  );
  const [selectedSize, setSelectedSize] = useState<string>(
    product.sizes && product.sizes.length > 0 ? product.sizes[0] : ''
  );
  const [addingToCart, setAddingToCart] = useState<boolean>(false);
  const [addedToCart, setAddedToCart] = useState<boolean>(false);
  const [activeImage, setActiveImage] = useState<string>(product.imageUrl || '');
  const [activeTab, setActiveTab] = useState<'description' | 'additional'>('description');

  const isOutOfStock = typeof product.inventory !== "undefined" && product.inventory <= 0;

  const handleAddToCart = (p: any) => {
    if (addingToCart || isOutOfStock) return;

    setAddingToCart(true);
    addToCart({ 
      ...p, 
      quantity: quantity,
      selectedColor: selectedColor,
      selectedSize: selectedSize
    });

    setTimeout(() => {
      setAddingToCart(false);
      setAddedToCart(true);
      setTimeout(() => {
        setAddedToCart(false);
      }, 2000);
    }, 400);
  };

  const incrementQuantity = () => {
    if (product.inventory && quantity >= product.inventory) return;
    setQuantity((prev) => prev + 1);
  };
  const decrementQuantity = () => setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  return (
    <div className="min-h-screen bg-white pt-24 pb-16">
      {/* ─── Breadcrumb Navigation ─── */}
      <div className="bg-[#F9F1E7] h-20 flex items-center mb-10">
        <div className="max-w-[1400px] w-full mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <ul className="flex items-center gap-3 text-sm text-gray-600 font-medium">
            <li>
              <a href="/" className="hover:text-[#B88E2F] transition-colors">Home</a>
            </li>
            <li>
              <Image src="/images/black-arr.png" alt="" width={14} height={14} />
            </li>
            <li>
              <a href="/shop" className="hover:text-[#B88E2F] transition-colors">Shop</a>
            </li>
            <li>
              <Image src="/images/black-arr.png" alt="" width={14} height={14} />
            </li>
            <li className="text-gray-900 font-bold border-l-2 border-gray-300 pl-3 truncate max-w-[200px] sm:max-w-xs">
              {toTitleCase(product.title)}
            </li>
          </ul>

          <button 
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-xs sm:text-sm font-bold text-gray-700 hover:text-[#B88E2F] transition-all bg-white hover:bg-gray-50 border border-gray-200 px-4 py-2 rounded-xl shadow-sm hover:shadow active:scale-95"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Back</span>
          </button>
        </div>
      </div>

      {/* ─── Main Product Layout ─── */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start justify-between">
          
          {/* LEFT: Image Gallery */}
          <div className="w-full lg:w-7/12 flex flex-col sm:flex-row gap-5">
            {/* Sidebar Thumbnails */}
            <div className="hidden sm:flex flex-col gap-3.5">
              {[product.imageUrl, ...(product.images || [])].filter(Boolean).map((imgUrl, index) => (
                <button
                  key={index}
                  onClick={() => setActiveImage(imgUrl)}
                  className={`w-20 h-20 border rounded-xl overflow-hidden p-1.5 transition-all duration-200 bg-gray-50 flex items-center justify-center ${
                    activeImage === imgUrl 
                      ? 'border-[#B88E2F] ring-2 ring-[#B88E2F]/10 scale-105' 
                      : 'border-gray-200 hover:border-gray-400'
                  }`}
                >
                  <Image
                    src={imgUrl}
                    alt={`Thumbnail ${index + 1}`}
                    width={70}
                    height={70}
                    className="w-full h-full object-contain"
                  />
                </button>
              ))}
            </div>

            {/* Large Preview Image */}
            <div className="flex-1 border border-gray-100 bg-[#F9F9F9] rounded-[24px] overflow-hidden flex items-center justify-center p-6 min-h-[350px] sm:min-h-[500px] relative">
              <Image
                src={activeImage || product.imageUrl}
                alt={product.title}
                width={500}
                height={500}
                priority
                className="max-w-full h-auto object-contain p-4 transition-transform duration-500 hover:scale-102"
              />
              {isOutOfStock && (
                <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center rounded-[24px]">
                  <span className="bg-red-600 text-white font-extrabold text-xs tracking-widest uppercase px-4 py-2 rounded-xl shadow-lg">
                    Out of Stock
                  </span>
                </div>
              )}
            </div>

            {/* Mobile Thumbnails */}
            <div className="flex sm:hidden gap-3 overflow-x-auto pb-1 mt-1">
              {[product.imageUrl, ...(product.images || [])].filter(Boolean).map((imgUrl, index) => (
                <button
                  key={index}
                  onClick={() => setActiveImage(imgUrl)}
                  className={`flex-shrink-0 w-16 h-16 relative border rounded-xl overflow-hidden p-1.5 bg-gray-50 ${
                    activeImage === imgUrl ? 'border-[#B88E2F] ring-2 ring-[#B88E2F]/10' : 'border-gray-200'
                  }`}
                >
                  <Image
                    src={imgUrl}
                    alt={`Thumbnail ${index + 1}`}
                    fill
                    className="object-contain p-1"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* RIGHT: Product Details */}
          <div className="w-full lg:w-5/12 flex flex-col">
            {/* Category */}
            {product.categoryName && (
              <span className="text-xs font-extrabold text-[#B88E2F] tracking-widest uppercase mb-2">
                {product.categoryName}
              </span>
            )}

            <h1 className="text-3xl sm:text-2xl font-bold text-gray-900 tracking-tight leading-tight">
              {toTitleCase(product.title)}
            </h1>

            {/* Price & Stock Badge */}
            <div className="flex flex-wrap items-center gap-4 mt-3.5 pb-5 border-b border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-2">
                <span className="text-2xl sm:text-3xl font-extrabold text-blue-600">
                  Rs. {product.price.toLocaleString()}
                </span>
                {product.discountPercentage && product.discountPercentage > 0 ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400 line-through">
                      Rs. {Math.round(product.price / (1 - product.discountPercentage / 100)).toLocaleString()}
                    </span>
                    <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-md">
                      {product.discountPercentage}% OFF
                    </span>
                  </div>
                ) : null}
              </div>
              
              <div className="sm:ml-auto">
                {isOutOfStock ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-150">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse"></span>
                    Out of Stock
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-150">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-600"></span>
                    In Stock
                    {product.inventory && product.inventory < 10 && ` (Only ${product.inventory} left)`}
                  </span>
                )}
              </div>
            </div>

            {/* Specs / Options */}
            {((product.sizes && product.sizes.length > 0) || (product.colors && product.colors.length > 0)) && (
              <div className="mt-8 space-y-6">
                {/* Size */}
                {product.sizes && product.sizes.length > 0 && (
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Size:</h3>
                    <div className="flex flex-wrap gap-2.5">
                      {product.sizes.map((size) => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`px-4 py-2 border rounded-xl text-xs font-bold transition-all duration-200 ${
                            selectedSize === size
                              ? 'border-black bg-black text-white'
                              : 'border-gray-200 hover:border-gray-400 bg-white text-gray-700'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Finish Colors */}
                {product.colors && product.colors.length > 0 && (
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Finish/Color:</h3>
                    <div className="flex flex-wrap items-center gap-3">
                      {product.colors.map((color) => {
                        const isHex = color.startsWith('#');
                        return (
                          <button
                            key={color}
                            className={`min-w-[2rem] h-8 rounded-full border-2 transition-all duration-200 hover:scale-105 relative flex items-center justify-center px-2 text-xs font-bold ${
                              selectedColor === color ? 'border-black ring-2 ring-offset-2 ring-[#B88E2F]' : 'border-gray-200'
                            }`}
                            style={isHex ? { backgroundColor: color } : undefined}
                            onClick={() => setSelectedColor(color)}
                          >
                            {!isHex && <span className={selectedColor === color ? 'text-black font-extrabold' : 'text-gray-600'}>{color}</span>}
                            {isHex && selectedColor === color && (
                              <span className="absolute inset-0 m-auto w-1.5 h-1.5 rounded-full bg-white shadow-sm" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Quantity and Cart Actions */}
            <div className="mt-8 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center gap-4">
              {/* Quantity Selector */}
              <div className="flex items-center justify-between border border-gray-200 rounded-2xl w-full sm:w-[130px] h-[56px] px-3 bg-gray-50/50">
                <button
                  disabled={isOutOfStock}
                  onClick={decrementQuantity}
                  className="w-8 h-8 flex items-center justify-center text-lg font-bold text-gray-500 hover:text-black hover:bg-gray-200/50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  -
                </button>
                <span className="font-extrabold text-gray-900 text-sm">
                  {isOutOfStock ? 0 : quantity}
                </span>
                <button
                  disabled={isOutOfStock}
                  onClick={incrementQuantity}
                  className="w-8 h-8 flex items-center justify-center text-lg font-bold text-gray-500 hover:text-black hover:bg-gray-200/50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>

              {/* Add To Cart */}
              <button
                disabled={addingToCart || isOutOfStock}
                onClick={() => handleAddToCart(product)}
                className={`flex-1 h-[56px] w-full rounded-2xl font-bold text-sm tracking-wider uppercase transition-all duration-300 shadow-sm flex items-center justify-center gap-2 ${
                  isOutOfStock
                    ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed shadow-none'
                    : addedToCart
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-black text-white hover:bg-gray-800 active:scale-95'
                }`}
              >
                {isOutOfStock ? (
                  'Sold Out'
                ) : addingToCart ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Adding...
                  </>
                ) : addedToCart ? (
                  <>
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    Added!
                  </>
                ) : (
                  'Add To Cart'
                )}
              </button>
            </div>

            {/* Attributes Spec List */}
            <div className="mt-8 pt-8 border-t border-gray-100 grid grid-cols-2 gap-y-3 gap-x-4 text-xs border-b border-gray-100 pb-8 mb-8">
              <span className="font-semibold text-gray-400">SKU:</span>
              <span className="font-bold text-gray-800">{product.sku || 'MB-PRD-882'}</span>

              <span className="font-semibold text-gray-400">Category:</span>
              <span className="font-bold text-gray-800">{product.categoryName || 'General Supply'}</span>

              <span className="font-semibold text-gray-400">Tags:</span>
              <span className="font-bold text-gray-800">{product.tags?.join(', ') || 'School, Office, Premium'}</span>

              <span className="font-semibold text-gray-400">Share:</span>
              <div className="flex items-center gap-2">
                {['fb', 'in', 'twi'].map((social) => (
                  <a key={social} href="#" className="hover:opacity-80 transition-opacity">
                    <Image
                      src={`/images/${social}.png`}
                      alt={social}
                      width={18}
                      height={18}
                    />
                  </a>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ─── Interactive Specification Tabs ─── */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
          {/* Tab Headers */}
          <div className="border-b border-gray-100 bg-[#FAF7F2]/50 flex justify-center sm:justify-start gap-8 px-6 sm:px-12 py-4">
            {[
              { id: 'description', label: 'Description' },
              { id: 'additional', label: 'Additional Information' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`pb-2 text-sm sm:text-base font-bold transition-all relative ${
                  activeTab === tab.id
                    ? 'text-black'
                    : 'text-gray-400 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-[#B88E2F] rounded-full animate-in fade-in" />
                )}
              </button>
            ))}
          </div>

          {/* Tab Content Display */}
          <div className="p-6 sm:p-12">
            {activeTab === 'description' && (
              <div className="max-w-4xl mx-auto space-y-6">
                <h3 className="text-lg font-bold text-gray-900">About the Product</h3>
                <p className="text-gray-600 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                  {product.description}
                </p>
              </div>
            )}

            {activeTab === 'additional' && (
              <div className="max-w-3xl mx-auto">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Technical Specifications</h3>
                <div className="border border-gray-100 rounded-2xl overflow-hidden divide-y divide-gray-100">
                  <div className="grid grid-cols-3 p-4 bg-gray-50/30">
                    <span className="font-semibold text-gray-500 text-sm">Product SKU</span>
                    <span className="col-span-2 font-bold text-gray-800 text-sm">{product.sku || 'MB-PRD-882'}</span>
                  </div>
                  <div className="grid grid-cols-3 p-4">
                    <span className="font-semibold text-gray-500 text-sm">Main Category</span>
                    <span className="col-span-2 font-bold text-gray-800 text-sm">{product.categoryName || 'General'}</span>
                  </div>
                  <div className="grid grid-cols-3 p-4 bg-gray-50/30">
                    <span className="font-semibold text-gray-500 text-sm">Stock Level</span>
                    <span className="col-span-2 font-bold text-gray-800 text-sm">
                      {product.inventory !== undefined ? `${product.inventory} units available` : 'In stock'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 p-4">
                    <span className="font-semibold text-gray-500 text-sm">Finishing Options</span>
                    <span className="col-span-2 font-bold text-gray-800 text-sm">Premium Oak, Sleek Black, Chrome Finish</span>
                  </div>
                  <div className="grid grid-cols-3 p-4 bg-gray-50/30">
                    <span className="font-semibold text-gray-500 text-sm">Product Origin</span>
                    <span className="col-span-2 font-bold text-gray-800 text-sm">Pakistan (Premium Local Supply)</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Related Products Section ─── */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 mt-20 relative">
        <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight text-center mb-10">
          Related Products
        </h2>
        <Asgaardproduct 
          currentProductId={product._id}
          categoryName={product.categoryName}
          tags={product.tags}
        />
      </div>

    </div>
  );
}
